/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Edit, Archive, RotateCcw, Flag, FlagOff, Send, History,
  RefreshCw, BarChart3, ListChecks, AlertTriangle, Loader2, Eye,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { API, getAuthHeader, STATUTS, FLAG_CATEGORIES, formatDate, formatEUR, StatusBadge } from './communityplaceShared';

export function EditDemandeModal({ modal, setModal, onDone }) {
  const { open, demande } = modal;
  const [form, setForm] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (demande) {
      setForm({
        type_demande: demande.type_demande || '',
        message: demande.message || demande.description || '',
        territoire: demande.territoire || '',
        ville: demande.ville || '',
        code_postal: demande.code_postal || '',
        prix_ht: demande.prix_ht ?? '',
        prix_ttc: demande.prix_ttc ?? '',
        prix_credits: demande.prix_credits ?? '',
      });
      setReason('');
    }
  }, [demande]);

  const close = () => setModal({ open: false, demande: null });

  const save = async () => {
    if (!demande) return;
    setSaving(true);
    try {
      const payload = { reason: reason || 'Édition Super Admin' };
      Object.keys(form).forEach((k) => {
        if (form[k] !== '' && form[k] !== null) {
          if (['prix_ht', 'prix_ttc', 'prix_credits'].includes(k)) {
            payload[k] = Number(form[k]);
          } else {
            payload[k] = form[k];
          }
        }
      });
      const res = await axios.patch(`${API}/${demande.id}`, payload, { headers: getAuthHeader() });
      const changed = Object.keys(res.data?.changes || {}).length;
      toast.success(changed > 0 ? `${changed} champ(s) modifié(s)` : 'Aucun changement');
      onDone();
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur édition');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !demande) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl" data-testid="sa-cp-edit-modal">
        <DialogHeader>
          <DialogTitle>Éditer la demande {demande.oscop_id || demande.id?.slice(0, 8)}</DialogTitle>
          <DialogDescription>Toutes les modifications sont loggées dans l'audit journal.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Type / Titre</Label>
            <Input value={form.type_demande || ''} onChange={(e) => setForm({ ...form, type_demande: e.target.value })} data-testid="sa-cp-edit-type" />
          </div>
          <div className="col-span-2">
            <Label>Message / Description</Label>
            <Textarea rows={3} value={form.message || ''} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="sa-cp-edit-message" />
          </div>
          <div>
            <Label>Territoire</Label>
            <Input value={form.territoire || ''} onChange={(e) => setForm({ ...form, territoire: e.target.value })} />
          </div>
          <div>
            <Label>Ville</Label>
            <Input value={form.ville || ''} onChange={(e) => setForm({ ...form, ville: e.target.value })} />
          </div>
          <div>
            <Label>Code postal</Label>
            <Input value={form.code_postal || ''} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} />
          </div>
          <div>
            <Label>Prix HT (€)</Label>
            <Input type="number" step="0.01" value={form.prix_ht ?? ''} onChange={(e) => setForm({ ...form, prix_ht: e.target.value })} data-testid="sa-cp-edit-prix-ht" />
          </div>
          <div>
            <Label>Prix TTC (€)</Label>
            <Input type="number" step="0.01" value={form.prix_ttc ?? ''} onChange={(e) => setForm({ ...form, prix_ttc: e.target.value })} data-testid="sa-cp-edit-prix-ttc" />
          </div>
          <div>
            <Label>Prix crédits</Label>
            <Input type="number" step="0.01" value={form.prix_credits ?? ''} onChange={(e) => setForm({ ...form, prix_credits: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Motif (audit)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optionnel" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="sa-cp-edit-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ArchiveDemandeModal({ modal, setModal, onDone }) {
  const { open, demande } = modal;
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const isArchived = !!demande?.is_archived;
  const close = () => { setReason(''); setModal({ open: false, demande: null }); };

  const submit = async () => {
    if (!demande) return;
    if (!isArchived && reason.trim().length < 3) { toast.error('Motif requis (>= 3 caractères)'); return; }
    setSaving(true);
    try {
      const url = `${API}/${demande.id}/${isArchived ? 'restore' : 'archive'}`;
      const body = isArchived ? {} : { reason };
      await axios.post(url, body, { headers: getAuthHeader() });
      toast.success(isArchived ? 'Demande restaurée' : 'Demande archivée');
      onDone(); close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  if (!open || !demande) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="sa-cp-archive-modal">
        <DialogHeader>
          <DialogTitle>{isArchived ? 'Restaurer la demande' : 'Archiver la demande'}</DialogTitle>
          <DialogDescription>
            {isArchived
              ? 'La demande sera réactivée et redeviendra visible dans la Communityplace.'
              : 'Cette demande sera masquée. Action réversible.'}
          </DialogDescription>
        </DialogHeader>
        {!isArchived && (
          <div>
            <Label>Motif d&apos;archivage</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: doublon, demande obsolète…" data-testid="sa-cp-archive-reason" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="sa-cp-archive-submit-btn"
            variant={isArchived ? 'default' : 'destructive'}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isArchived ? 'Restaurer' : 'Archiver')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FlagDemandeModal({ modal, setModal, onDone }) {
  const { open, demande } = modal;
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState('abus');
  const [saving, setSaving] = useState(false);
  const isFlagged = !!demande?.is_flagged;
  const close = () => { setReason(''); setCategory('abus'); setModal({ open: false, demande: null }); };

  const submit = async () => {
    if (!demande) return;
    if (!isFlagged && reason.trim().length < 3) { toast.error('Motif requis (>= 3 caractères)'); return; }
    setSaving(true);
    try {
      const url = `${API}/${demande.id}/${isFlagged ? 'unflag' : 'flag'}`;
      const body = isFlagged ? {} : { reason, category };
      await axios.post(url, body, { headers: getAuthHeader() });
      toast.success(isFlagged ? 'Signalement levé' : 'Demande signalée');
      onDone(); close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  if (!open || !demande) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="sa-cp-flag-modal">
        <DialogHeader>
          <DialogTitle>{isFlagged ? 'Lever le signalement' : 'Signaler la demande'}</DialogTitle>
          <DialogDescription>
            {isFlagged ? 'La demande sera dé-signalée et retirée de la liste de modération.'
              : 'Marque la demande pour modération. Visible dans l&apos;onglet &quot;Signalements&quot;.'}
          </DialogDescription>
        </DialogHeader>
        {!isFlagged && (
          <div className="space-y-3">
            <div>
              <Label>Catégorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="sa-cp-flag-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FLAG_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motif</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sa-cp-flag-reason" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="sa-cp-flag-submit-btn"
            variant={isFlagged ? 'default' : 'destructive'}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isFlagged ? 'Lever' : 'Signaler')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ForceRemiseModal({ modal, setModal, onDone }) {
  const { open, demande } = modal;
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const close = () => { setReason(''); setModal({ open: false, demande: null }); };

  const submit = async () => {
    if (!demande) return;
    if (reason.trim().length < 3) { toast.error('Motif requis (>= 3 caractères)'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/${demande.id}/force-remise`, { reason }, { headers: getAuthHeader() });
      toast.success('Lead remis sur la Communityplace');
      onDone(); close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  if (!open || !demande) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="sa-cp-force-remise-modal">
        <DialogHeader>
          <DialogTitle>Forcer la remise sur la Communityplace</DialogTitle>
          <DialogDescription>
            Le cooper actuellement assigné sera libéré (sans débit de crédits). La demande
            redevient publiée.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Motif</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: cooper inactif, demande mal qualifiée…" data-testid="sa-cp-remise-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="sa-cp-remise-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remettre sur Communityplace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HistoryModal({ modal, setModal }) {
  const { open, demande } = modal;
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const close = () => { setDetail(null); setModal({ open: false, demande: null }); };

  useEffect(() => {
    if (!open || !demande) return;
    setLoading(true);
    axios.get(`${API}/${demande.id}`, { headers: getAuthHeader() })
      .then((res) => setDetail(res.data))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Erreur'))
      .finally(() => setLoading(false));
  }, [open, demande]);

  if (!open || !demande) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="sa-cp-history-modal">
        <DialogHeader>
          <DialogTitle>Historique — {demande.oscop_id || demande.id?.slice(0, 8)}</DialogTitle>
          <DialogDescription>{demande.type_demande} · {demande.email}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : detail ? (
          <div className="space-y-4 text-sm">
            <section>
              <h3 className="font-semibold mb-2">Achats ({detail.achats?.length || 0})</h3>
              {detail.achats?.length ? (
                <div className="space-y-2">
                  {detail.achats.map((a) => (
                    <div key={a.id} className="p-2 bg-gray-50 rounded text-xs">
                      <div><strong>{formatDate(a.created_at)}</strong> · {a.statut_paiement || a.statut} · {formatEUR(a.prix_ttc)}</div>
                      <div className="text-gray-500">Cooper: {a.cooper_email || a.cooper_id || '—'}</div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-gray-500">Aucun achat</div>}
            </section>
            <section>
              <h3 className="font-semibold mb-2">Audit journal ({detail.audit?.length || 0})</h3>
              {detail.audit?.length ? (
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {detail.audit.map((a) => (
                    <div key={a.id} className="text-xs p-2 border-l-2 border-blue-400 bg-blue-50">
                      <div><strong>{formatDate(a.timestamp)}</strong> · {a.action_type}</div>
                      <div className="text-gray-600">{a.action_label}</div>
                      <div className="text-gray-500">par {a.user_email || a.user_nom || '—'}</div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-gray-500">Aucun événement audit</div>}
            </section>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={close}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// ONGLET STATS
// =============================================================================
